import { Button, Checkbox, Dropdown, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { useAuth } from '../../hooks/useAuth';
import { useTags } from '../../hooks/useTags';
import File from '../../interfaces/File';
import { getContrastBackgroundColorForText } from '../../utils/colorsUtilis';
import { Icon } from '../Core/Icon';
import TruncatedTooltip from '../Common/TruncatedTooltip';

interface FilesTableActionsCellProps {
  selectedFiles: File[];
  file: File;
  handleFileMenuAction: (
    filesIds: number[],
    action: FileMenuActionEnum,
    updatedTagIds?: number[]
  ) => void;
  handleRowContextMenu: (file: File, e: React.MouseEvent) => void;
}

const FilesTableActionsCell: React.FC<FilesTableActionsCellProps> = ({
  selectedFiles,
  file,
  handleFileMenuAction,
  handleRowContextMenu,
}) => {
  const { t } = useTranslation();
  const { tagsBasedOnUser, isTagDisabledForFile } = useTags();
  const { isInFileRoute, authenticatedUser, isAdminOrContributor } = useAuth();
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  /**
   * @description Check if there are changes in the tags.
   */
  const hasTagChanges = useMemo(() => {
    const original: number[] = file?.tags || [];
    const selected: number[] = selectedTagIds;

    if (original.length !== selected.length) return true;

    return !original.every((id) => selected.includes(id));
  }, [file?.tags, selectedTagIds]);

  /**
   * @description Tags item.
   */
  const tagsItem = useMemo(() => {
    return tagsBasedOnUser.map((tag) => ({
      key: tag.id,
      label: (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          key={tag.id}
          className="flex w-full cursor-pointer items-center justify-center gap-1 lg:justify-between"
        >
          <Checkbox
            className="hidden lg:inline-flex"
            aria-label={tag.title}
            checked={selectedTagIds.includes(tag.id)}
            onChange={(event) => {
              if (isTagDisabledForFile(file, tag)) return;
              if (event.target.checked) {
                setSelectedTagIds([...selectedTagIds, tag.id]);
              } else {
                setSelectedTagIds(selectedTagIds.filter((id) => id !== tag.id));
              }
            }}
          />
          <Tag
            key={tag.id}
            style={{
              backgroundColor: getContrastBackgroundColorForText(tag?.color),
            }}
            className={clsx(
              'relative w-full transition-all duration-300',
              !tag.fileVisible &&
                'pointer-events-none absolute left-0 top-0 w-full bg-[repeating-linear-gradient(-45deg,transparent,transparent_5px,#ccc_5px,#ccc_10px)] opacity-50 mix-blend-multiply',
              isTagDisabledForFile(file!, tag) &&
                'pointer-events-none opacity-50'
            )}
            onClick={() => {
              if (isTagDisabledForFile(file!, tag)) return;
              if (selectedTagIds.includes(tag.id)) {
                setSelectedTagIds(selectedTagIds.filter((id) => id !== tag.id));
              } else {
                setSelectedTagIds([...selectedTagIds, tag.id]);
              }
            }}
          >
            <div className="flex w-full flex-row items-center justify-center lg:justify-between">
              <div />
              <span
                className="hidden max-w-28 text-center lg:block"
                style={{
                  color: tag.color,
                }}
              >
                <TruncatedTooltip>{tag.title}</TruncatedTooltip>
              </span>
              <span
                style={{
                  color: tag.color,
                }}
                className="lg:hidden"
              >
                <TruncatedTooltip>{tag.title}</TruncatedTooltip>
              </span>
              <div className="hidden lg:block">
                {tag.publicTag ? (
                  <Icon
                    name="BuildingIcon"
                    className="h-4 w-4 text-gray-dark"
                  />
                ) : (
                  <div className="w-1" />
                )}
              </div>
            </div>
          </Tag>
          <div className="w-5" />
        </div>
      ),
    }));
  }, [tagsBasedOnUser, selectedTagIds]);

  /**
   * @description Check if the share button is visible.
   */
  const isShareVisible = useMemo(() => {
    return (
      !isInFileRoute(FilesTableEnum.Received) &&
      !isInFileRoute(FilesTableEnum.Trash) &&
      !isInFileRoute(FilesTableEnum.Hidden) &&
      !file.isQuickShare &&
      isAdminOrContributor &&
      file?.ownerId === authenticatedUser?.userId
    );
  }, [file, isInFileRoute, authenticatedUser, isAdminOrContributor]);

  useEffect(() => {
    if (file?.tags?.length && file.tags.length > 0) {
      setSelectedTagIds(file.tags);
    } else {
      setSelectedTagIds([]);
    }
  }, [file?.tags]);

  return (
    <>
      <div
        className={clsx(
          'actions-cell-content absolute right-0 top-0 z-20 flex h-full flex-row items-center justify-center border-l border-gray-medium bg-[#eeeffa] bg-gradient-to-r from-transparent to-[#eeeffa] px-2'
        )}
      >
        {isShareVisible && (
          <Button
            size="small"
            className="mr-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileMenuAction([file.uid], FileMenuActionEnum.Share);
            }}
          >
            {t('common.share')}
          </Button>
        )}
        {!file.isQuickShare && (
          <>
            {file.isFavourite ? (
              <Tooltip title="Remove from favorites">
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFileMenuAction(
                      [file.uid],
                      FileMenuActionEnum.RemoveFromFavorites
                    );
                  }}
                >
                  <Icon name="StarOffIcon" className="w-5" />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Add to favorites">
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFileMenuAction(
                      [file.uid],
                      FileMenuActionEnum.AddToFavorites
                    );
                  }}
                >
                  <Icon name="StarIcon" className="w-5" />
                </Button>
              </Tooltip>
            )}
          </>
        )}
        <Dropdown
          //getPopupContainer={(triggerNode) => triggerNode.parentElement!}
          menu={{
            items: [
              ...tagsItem,
              {
                key: 'divider',
                type: 'divider',
              },
              {
                key: FileMenuActionEnum.SaveTags,
                label: (
                  <Button
                    type="link"
                    size="small"
                    className="w-full text-center"
                    disabled={!hasTagChanges}
                  >
                    Update Tags
                  </Button>
                ),
                icon: null,
                type: 'item',
                onClick: () => {
                  handleFileMenuAction(
                    [file.uid],
                    FileMenuActionEnum.SaveTags,
                    selectedTagIds
                  );
                },
              },
            ],
          }}
        >
          <Button type="text" size="small">
            <Icon name="BookmarkIcon" className="h-4 w-4" />
          </Button>
        </Dropdown>
        <Button
          type="text"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleRowContextMenu(file, e);
          }}
        >
          <Icon name="EllipsisVerticalIcon" className="w-5" />
        </Button>
      </div>
    </>
  );
};

export default FilesTableActionsCell;
